import { z } from 'zod/v4';

import { FileLifecycleRuntype, FileLifecycleStateRuntype, HistoricLifecycleStateRuntype } from './pcbBackendTypes';

// Runtypes for the Stackrate AssemblyService lifecycle reads. These belong to the PCB domain
// (the Stackrate "assembly" is a different concept from the backend-core Assembly resource).
// They mirror the Lagom JSON wire shapes from the Scala sources:
//   common/impl-utils/.../internal/File.scala  (FileLifecycleStage(Name), ApiFileLifecycle)
//   framework/assembly/api/.../AssemblyLifecycleStageName.scala
//   framework/assembly/api/.../IndividualAssemblyLifecycleStage.scala
//   framework/assembly/api/.../AssemblyApi.scala  (LifecycleSummary)
// The shared status/history/stage primitives already live in pcbBackendTypes and are reused
// above. Scala `Option` fields are omitted when None, hence `.optional()`.

// File-level lifecycle stage names — the Scala reads rejects unknown values.
export const FileLifecycleStageNameRuntype = z.enum(['render', 'preview', 'fileanalysis']);
export type FileLifecycleStageName = z.infer<typeof FileLifecycleStageNameRuntype>;

// Board/version-level lifecycle stage names — the `.value` strings of the Scala sealed trait.
// `quotation` is deprecated but still deserialized, so it stays.
export const AssemblyLifecycleStageNameRuntype = z.enum([
    'dfm',
    'reconciliation',
    'analysis',
    'files',
    'fileanalysis',
    'layerstack',
    'outline',
    'render',
    'preview',
    'specification-render',
    'reconciled-specification-render',
    'initialization',
    'production-analysis',
    'customer-panel',
    'quotation',
]);
export type AssemblyLifecycleStageName = z.infer<typeof AssemblyLifecycleStageNameRuntype>;

// A single per-file lifecycle stage (getFileLifecycles / getFileLifecycle).
// Distinct from the pcb `FileLifecycleRuntype`: here `name` is the strict
// file-stage enum (render/preview/fileanalysis), not the broad union.
export const FileLifecycleStageRuntype = z.object({
    name: FileLifecycleStageNameRuntype,
    status: FileLifecycleStateRuntype,
    history: z.array(HistoricLifecycleStateRuntype).optional(),
});
export type FileLifecycleStage = z.infer<typeof FileLifecycleStageRuntype>;

// The map value returned by getVersionLifecycles (Map[String, ApiFileLifecycle]).
// `name` is a plain string here, plus a per-stage `durations` map.
export const ApiFileLifecycleRuntype = z.object({
    name: z.string(),
    status: FileLifecycleStateRuntype,
    durations: z.record(z.string(), z.number()),
    history: z.array(HistoricLifecycleStateRuntype).optional(),
});
export type ApiFileLifecycle = z.infer<typeof ApiFileLifecycleRuntype>;

// A single board-level stage (getVersionLifecycle) — `name` is the strict enum.
export const IndividualAssemblyLifecycleStageRuntype = z.object({
    name: AssemblyLifecycleStageNameRuntype,
    status: FileLifecycleStateRuntype,
    history: z.array(HistoricLifecycleStateRuntype).optional(),
});
export type IndividualAssemblyLifecycleStage = z.infer<typeof IndividualAssemblyLifecycleStageRuntype>;

// Compact overview returned by getLifecycleSummary. `version`/`aggregates` carry
// the broad-named stage values (reused from pcb); `files` is a nested
// file → stage-name → file-stage map.
export const LifecycleSummaryRuntype = z.object({
    version: z.record(z.string(), FileLifecycleRuntype),
    aggregates: z.record(z.string(), FileLifecycleRuntype),
    files: z.record(z.string(), z.record(z.string(), FileLifecycleStageRuntype)),
});
export type LifecycleSummary = z.infer<typeof LifecycleSummaryRuntype>;
